import React from 'react';

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular';
  width?: string | number;
  height?: string | number;
  lines?: number;
}

/**
 * Premium skeleton loading component with shimmer animation
 * Use for content placeholders while data loads
 */
export const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  variant = 'rectangular',
  width,
  height,
  lines = 1
}) => {
  const baseStyles = 'animate-shimmer bg-gradient-to-r from-neutral-200 via-neutral-100 to-neutral-200 bg-[length:200%_100%]';

  const variantStyles = {
    text: 'h-4 rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-lg'
  };

  const style: React.CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
  };

  if (lines > 1) {
    return (
      <div className="space-y-3">
        {Array.from({ length: lines }).map((_, i) => (
          <div
            key={i}
            className={`${baseStyles} ${variantStyles.text} ${className}`}
            style={{
              ...style,
              width: i === lines - 1 ? '70%' : '100%',
              height: height || 16
            }}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className={`${baseStyles} ${variantStyles[variant]} ${className}`}
      style={style}
    />
  );
};

/**
 * Card skeleton for dashboard widgets and cards
 */
export const CardSkeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`card ${className}`}>
    <div className="flex items-center gap-4 mb-4">
      <Skeleton variant="circular" width={48} height={48} />
      <div className="flex-1">
        <Skeleton height={20} width="60%" className="mb-2" />
        <Skeleton height={14} width="40%" />
      </div>
    </div>
    <Skeleton height={80} className="mb-4" />
    <Skeleton lines={2} />
  </div>
);

/**
 * Stats skeleton for metric cards
 */
export const StatsSkeleton: React.FC = () => (
  <div className="card">
    <Skeleton height={14} width="50%" className="mb-3" />
    <Skeleton height={36} width="40%" className="mb-2" />
    <Skeleton height={12} width="70%" />
  </div>
);


/**
 * Placeholder for a quiz question while the adaptive engine picks the next one.
 * The old treatment left the ANSWERED question on screen, dimmed, which read as
 * a frozen page rather than a load: a skeleton says "new content is coming".
 * Mirrors QuestionCard's anatomy (stem block + four option rows).
 */
export const QuestionCardSkeleton: React.FC = () => (
  <div className="card" data-testid="question-card-skeleton" aria-hidden="true">
    <Skeleton height={14} width="30%" className="mb-4" />
    <Skeleton lines={3} height={18} className="mb-6" />
    <div className="space-y-3 mt-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} height={56} />
      ))}
    </div>
  </div>
);

/**
 * Dashboard skeleton layout
 */
export const DashboardSkeleton: React.FC = () => (
  <div className="container-base py-8" data-testid="dashboard-skeleton">
    {/* Header skeleton */}
    <div className="mb-8">
      <Skeleton height={36} width="50%" className="mb-3" />
      <Skeleton height={20} width="30%" />
    </div>

    {/* Stats row */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      <StatsSkeleton />
      <StatsSkeleton />
      <StatsSkeleton />
    </div>

    {/* Main content */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <CardSkeleton />
      <CardSkeleton />
    </div>
  </div>
);

export default Skeleton;
